import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, InputGroup, Alert, Spinner } from 'react-bootstrap';
import { FaSearch, FaDownload, FaTable } from 'react-icons/fa';
import axios from 'axios';

const ArtikelExport = () => {
  const [artikel, setArtikel] = useState([]);
  const [filteredArtikel, setFilteredArtikel] = useState([]);
  const [suchbegriff, setSuchbegriff] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Artikel vom Backend laden
  const loadArtikel = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get('/api/artikel');
      setArtikel(response.data);
      setFilteredArtikel(response.data);
    } catch (err) {
      console.error('Fehler beim Laden der Artikel:', err);
      setError('Die Artikel konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Beim ersten Rendern Artikel laden
  useEffect(() => {
    loadArtikel();
  }, []);

  // Artikel filtern basierend auf Suchbegriff
  useEffect(() => {
    if (suchbegriff.trim() === '') {
      setFilteredArtikel(artikel);
    } else {
      const lowerSuchbegriff = suchbegriff.toLowerCase();
      const filtered = artikel.filter(item => 
        item.name.toLowerCase().includes(lowerSuchbegriff) ||
        (item.artikelnummer && item.artikelnummer.toLowerCase().includes(lowerSuchbegriff)) ||
        (item.kategorie && item.kategorie.toLowerCase().includes(lowerSuchbegriff)) ||
        (item.lagerort && item.lagerort.toLowerCase().includes(lowerSuchbegriff))
      );
      setFilteredArtikel(filtered);
    }
  }, [suchbegriff, artikel]);

  // CSV-Datei erstellen und herunterladen (UTF-8)
  const exportCSV = () => {
    if (!filteredArtikel || filteredArtikel.length === 0) {
      alert('Keine Artikel zum Exportieren vorhanden.');
      return;
    }

    try {
      // Kopfzeilen für CSV definieren - alle Felder aus dem Artikelformular
      const headers = [
        'ID',
        'Artikelnummer',
        'Name',
        'Beschreibung',
        'Kategorie',
        'Lagerort',
        'Lieferant',
        'Lagerbestand',
        'Einheit',
        'Preis',
        'Online-Shop Link',
        'Für Etikettendruck',
        'EAN/GTIN'
      ].join(',');

      // Daten formatieren und in CSV konvertieren - alle Felder aus dem Artikelformular
      const csvData = filteredArtikel.map(item => {
        return [
          item.id || '',
          item.artikelnummer || '',
          `"${(item.name || '').replace(/"/g, '""')}"`, // Anführungszeichen escapen
          `"${(item.beschreibung || '').replace(/"/g, '""')}"`,
          `"${(item.kategorie || '').replace(/"/g, '""')}"`,
          `"${(item.lagerort || '').replace(/"/g, '""')}"`,
          `"${(item.lieferant || '').replace(/"/g, '""')}"`,
          item.lagerbestand || '0',
          item.einheit || 'Stk.',
          item.preis || '0.00',
          `"${(item.onlineshoplink || '').replace(/"/g, '""')}"`, // Online-Shop Link
          item.fuerEtikettendruck ? 'Ja' : 'Nein', // Für Etikettendruck
          item.ean || ''
        ].join(',');
      });

      // CSV-Inhalt erstellen
      const csvContent = [headers, ...csvData].join('\n');
      
      // UTF-8 BOM (Byte Order Mark) hinzufügen für bessere Kompatibilität
      const BOM = '\uFEFF';
      const csvContentWithBOM = BOM + csvContent;

      // Download initiieren mit expliziter UTF-8-Kodierung
      const blob = new Blob([csvContentWithBOM], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      
      // Datum für Dateinamen
      const heute = new Date();
      const datum = heute.toISOString().slice(0, 10);
      
      link.href = url;
      link.setAttribute('download', `Artikeldaten_Export_UTF-8_${datum}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Fehler beim CSV-Export:', err);
      alert('Fehler beim Erstellen der CSV-Datei.');
    }
  };

  return (
    <Container>
      <h1 className="mb-4">Artikeldaten-Export</h1>
      
      {error && <Alert variant="danger">{error}</Alert>}
      
      <Card className="shadow-sm mb-4">
        <Card.Header className="bg-light">
          <Row className="align-items-center">
            <Col>
              <h5 className="mb-0">Datensicherung & Export</h5>
            </Col>
          </Row>
        </Card.Header>
        <Card.Body>
          <p>
            Auf dieser Seite können Sie alle Artikeldaten einsehen und als CSV-Datei exportieren.
            Dies ist nützlich für Datensicherungen oder zur weiteren Verarbeitung in anderen Programmen.
          </p>
          <Alert variant="info">
            <strong>Exportierte Felder:</strong> ID, Artikelnummer, Name, Beschreibung, Kategorie, Lagerort, 
            Lieferant, Lagerbestand, Einheit, Preis, Online-Shop Link, Für Etikettendruck, EAN/GTIN
          </Alert>
          <Button 
            variant="success" 
            className="mb-3" 
            onClick={exportCSV}
            disabled={loading || filteredArtikel.length === 0}
          >
            <FaDownload className="me-2" /> CSV UTF-8 (durch Trennzeichen getrennt)
          </Button>
        </Card.Body>
      </Card>
      
      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Artikel suchen..."
              value={suchbegriff}
              onChange={(e) => setSuchbegriff(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="d-flex justify-content-md-end align-items-center mt-3 mt-md-0">
          <span className="text-muted">
            {filteredArtikel.length} {filteredArtikel.length === 1 ? 'Artikel' : 'Artikel'} gefunden
          </span>
        </Col>
      </Row>
      
      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Laden...</span>
          </Spinner>
          <p className="mt-2">Artikel werden geladen...</p>
        </div>
      ) : filteredArtikel.length === 0 ? (
        <Card className="text-center p-4 shadow-sm">
          <Card.Body>
            <h5>Keine Artikel gefunden</h5>
            <p className="text-muted">
              {suchbegriff ? 
                `Es wurden keine Artikel gefunden, die "${suchbegriff}" enthalten.` : 
                'Es sind noch keine Artikel im System erfasst.'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div className="table-responsive">
          <Card className="shadow-sm">
            <Card.Body className="p-0">
              <Table hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Artikelnummer</th>
                    <th>Name</th>
                    <th>Kategorie</th>
                    <th>Lagerbestand</th>
                    <th>Einheit</th>
                    <th>Preis</th>
                    <th>Lieferant</th>
                    <th>Lagerort</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredArtikel.map(artikel => (
                    <tr key={artikel.id}>
                      <td>{artikel.artikelnummer || '-'}</td>
                      <td>{artikel.name}</td>
                      <td>{artikel.kategorie || '-'}</td>
                      <td>{artikel.lagerbestand || '0'}</td>
                      <td>{artikel.einheit || 'Stk.'}</td>
                      <td>{artikel.preis ? `${artikel.preis.toFixed(2)} €` : '-'}</td>
                      <td>{artikel.lieferant || '-'}</td>
                      <td>{artikel.lagerort || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </div>
      )}
    </Container>
  );
};

export default ArtikelExport;
